import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Trophy, Clock, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

export default function ExamResult() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResult();
  }, []);

  const fetchResult = async () => {
    try {
      const response = await axios.get(`${API}/student/result/${attemptId}`);
      setResult(response.data);
    } catch (error) {
      toast.error('Failed to fetch result');
      navigate('/student/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!result) return null;

  const percentage = (result.attempt.score / result.exam.total_marks * 100).toFixed(1);
  const passed = percentage >= 50;
  const timeSpent = Math.floor(result.attempt.time_spent_seconds / 60);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <Button 
          variant="outline" 
          onClick={() => navigate('/student/dashboard')}
          className="mb-6 border-sky-200"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Score Summary */}
        <Card className="border-sky-100 shadow-lg mb-6">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className={`p-4 rounded-full ${
                passed ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <Trophy className={`h-12 w-12 ${
                  passed ? 'text-green-600' : 'text-red-600'
                }`} />
              </div>
            </div>
            <CardTitle className="text-3xl mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              {result.exam.title}
            </CardTitle>
            <Badge className={`text-lg py-1 px-4 ${
              passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {passed ? 'Passed' : 'Failed'}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="space-y-2">
                <p className="text-gray-600">Your Score</p>
                <p className="text-4xl font-bold text-sky-600" data-testid="result-score">
                  {result.attempt.score}/{result.exam.total_marks}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-gray-600">Percentage</p>
                <p className="text-4xl font-bold text-sky-600" data-testid="result-percentage">
                  {percentage}%
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-gray-600">Time Spent</p>
                <p className="text-4xl font-bold text-sky-600" data-testid="result-time">
                  {timeSpent} min
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Question Review */}
        <Card className="border-sky-100 shadow-lg">
          <CardHeader>
            <CardTitle>Question Review</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {result.exam.questions.map((question, idx) => {
                const userAnswer = result.attempt.answers[question.id]?.answer || 'Not answered';
                const isCorrect = userAnswer.trim().toLowerCase() === question.correct_answer.trim().toLowerCase();
                
                return (
                  <div key={question.id} className="border border-gray-200 rounded-lg p-4" data-testid={`question-review-${idx + 1}`}>
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-semibold text-gray-900">Q{idx + 1}. {question.question_text}</h4>
                      {isCorrect ? (
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" data-testid={`correct-${idx + 1}`} />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-testid={`incorrect-${idx + 1}`} />
                      )}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">Your Answer: </span>
                        <span className={isCorrect ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                          {userAnswer}
                        </span>
                      </div>
                      {!isCorrect && (
                        <div>
                          <span className="text-gray-600">Correct Answer: </span>
                          <span className="text-green-600 font-semibold">{question.correct_answer}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600">Marks: </span>
                        <span>{isCorrect ? question.marks : 0}/{question.marks}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}